import { useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"
import { Link } from "react-router"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authProvider } from "@/providers/auth.provider"

interface ForgotPasswordPayload {
  email: string
}

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { register, handleSubmit } = useForm<ForgotPasswordPayload>()

  const { mutate, isPending, error, isSuccess } = useMutation({
    mutationFn: (data: ForgotPasswordPayload) =>
      authProvider.initResetPassword(data.email),
  })

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>Reset your password</CardTitle>
          <CardDescription>
            Enter your email and we'll send you a reset link
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <p className="text-sm text-center">
              Check your email for a reset link
            </p>
          ) : (
            <form onSubmit={handleSubmit((data) => mutate(data))}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder="m@example.com"
                    {...register("email", { required: true })}
                  />
                </Field>
                {error && (
                  <p className="text-sm text-destructive">
                    Please enter a valid email address
                  </p>
                )}
                <Field>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? "Sending…" : "Send reset link"}
                  </Button>
                </Field>
              </FieldGroup>
            </form>
          )}
          <p className="mt-4 text-center text-sm">
            <Link
              to="/auth"
              className="underline-offset-4 hover:underline"
            >
              Back to login
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
